/**
 * AssessmentCompletionOverlay Component - Transition Overlay
 * @module features/chat/components/AssessmentCompletionOverlay
 *
 * Overlay de transição exibido quando o assessment é concluído.
 * Mostra spinner e mensagem enquanto prepara navegação para resultados.
 *
 * T002-FE: Detecção de Fim do Assessment (US-007)
 */

import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Props do componente AssessmentCompletionOverlay
 */
export interface AssessmentCompletionOverlayProps {
  /** Se o overlay deve ser exibido */
  isVisible: boolean;

  /** Mensagem customizada (opcional) */
  message?: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/**
 * Mensagem padrão exibida durante transição
 */
const DEFAULT_MESSAGE = 'Consolidando informações para gerar o relatório...';

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Overlay de conclusão do assessment com spinner e mensagem
 *
 * Features:
 * - Overlay full-screen com backdrop blur
 * - Spinner animado
 * - Mensagem customizável
 * - Acessibilidade completa (role="status", aria-live)
 * - Animação de fade-in
 * - Focus trap (aria-modal)
 *
 * @example
 * ```tsx
 * <AssessmentCompletionOverlay
 *   isVisible={isTransitioning}
 *   message="Processando suas respostas..."
 * />
 * ```
 */
export function AssessmentCompletionOverlay({
  isVisible,
  message = DEFAULT_MESSAGE,
}: AssessmentCompletionOverlayProps) {
  if (!isVisible) {
    return null;
  }

  return (
    <output
      data-testid="completion-overlay"
      aria-live="polite"
      aria-modal="true"
      className={cn(
        // Posicionamento
        'fixed inset-0 z-50',
        // Flexbox para centralizar
        'flex items-center justify-center',
        // Background com blur
        'bg-background/95 backdrop-blur-sm',
        // Animacao
        'animate-in fade-in duration-300'
      )}
    >
      <div
        className={cn(
          'flex flex-col items-center gap-6',
          'p-8 rounded-lg',
          'max-w-md mx-4 text-center'
        )}
      >
        {/* Spinner */}
        <div data-testid="completion-spinner" className="relative" aria-hidden="true">
          <Loader2 className={cn('h-16 w-16 animate-spin', 'text-primary')} />
        </div>

        {/* Mensagem */}
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-foreground">Chegamos ao fim do review</h2>
          <p className="text-muted-foreground">{message}</p>
        </div>

        {/* Texto acessível adicional */}
        <span className="sr-only">Por favor, aguarde enquanto preparamos seus resultados.</span>
      </div>
    </output>
  );
}

export default AssessmentCompletionOverlay;
